import { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { fetchSEOData, SEOData } from '../services/seoService';
import { FallbackMetadata } from '../types/ContentsInterface';

interface UseSEOOptions {
  fallbackMetadata?: FallbackMetadata;
  slug?: string;
  noindex?: boolean;
  nofollow?: boolean;
}

export function useSEO(options: UseSEOOptions = {}) {
  const { fallbackMetadata, slug, noindex = false, nofollow = false } = options;
  const location = useLocation();
  const [seoData, setSeoData] = useState<SEOData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastFetchedKey = useRef<string>('');

  useEffect(() => {
    const loadSEOData = async () => {
      const currentKey = `${location.pathname}-${slug || ''}`;
      if (lastFetchedKey.current === currentKey) {
        return;
      }
      lastFetchedKey.current = currentKey;
      try {
        setLoading(true);
        setError(null);

        const pathname = location.pathname;
        const data = await fetchSEOData(pathname, slug, fallbackMetadata);
        setSeoData(data);
      } catch (err) {
        console.error('Error loading SEO data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load SEO data');

        // Set fallback data even on error
        const fallbackData: SEOData = {
          title: fallbackMetadata?.title || 'AI For Marketers | gimmefy',
          description: fallbackMetadata?.description || 'Default Description',
          keywords: fallbackMetadata?.keywords || 'Default Keywords',
          imageUrl: 'https://gimmefy.ai/server-assets/images/gimmefy-logo.png',
        };
        setSeoData(fallbackData);
      } finally {
        setLoading(false);
      }
    };

    loadSEOData();
  }, [location.pathname, slug, fallbackMetadata]);

  const canonicalUrl = `${window.location.origin}${location.pathname}`;

  return {
    seoData,
    loading,
    error,
    canonicalUrl,
    noindex,
    nofollow,
  };
}
